import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'light' | 'dark';

/** index.html 의 인라인 스크립트와 반드시 같은 키를 써야 한다 (거기서 첫 페인트 전에 먼저 읽는다). */
const STORAGE_KEY = 'xadmin-theme';

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    // 시크릿 모드 등 localStorage 를 막아둔 브라우저 - 저장 없이 그냥 진행한다.
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  // index.html 의 인라인 스크립트가 이미 <html data-theme> 를 채워 놨으므로, 여기서도
  // 같은 규칙(저장값 > 시스템 설정)으로 계산하면 첫 렌더부터 그 값과 일치한다.
  const [theme, setThemeState] = useState<Theme>(() => readStored() ?? systemTheme());

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    // 사용자가 명시적으로 고른 적이 없을 때만 시스템 설정 변화를 계속 따라간다 -
    // 한 번이라도 저장된 값이 있으면(=명시적 선택) 시스템이 바뀌어도 그대로 둔다.
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      if (readStored()) return;
      setThemeState(e.matches ? 'dark' : 'light');
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // 저장이 안 돼도 이번 세션 안에서는 그대로 적용한다.
    }
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme 은 ThemeProvider 안에서만 쓸 수 있다.');
  return ctx;
}
